/**
 * Escape leert ein Textfeld — geteiltes Verhalten aller Felder mit Clear-✕.
 *
 * Liefert die Props fürs `<input>`: Escape löst dieselbe Aktion aus wie ein Klick
 * aufs ✕. Der Fokus bleibt im Feld, damit direkt weitergetippt werden kann.
 *
 * Beispiel:
 *   <input
 *     value={value}
 *     {...clearOnEscape({ hasValue: Boolean(value), clear: () => onChange('') })}
 *   />
 *
 * Params:
 *   hasValue   — gibt es überhaupt etwas zu leeren (dieselbe Bedingung wie das ✕)
 *   clear      — leert das Feld
 *   onKeyDown  — optionaler Handler des Konsumenten; läuft zuerst und behält Vorrang
 */
export const CLEARABLE_FIELD_ATTR = 'data-clear-on-escape';

export default function clearOnEscape({ hasValue, clear, onKeyDown }) {
  return {
    [CLEARABLE_FIELD_ATTR]: hasValue ? 'true' : undefined,
    onKeyDown: (event) => {
      // Nur ein preventDefault AUS DEM KONSUMENTEN-HANDLER zählt als „schon behandelt".
      // Beim Escape steht `defaultPrevented` hier oft schon auf true — die Radix-Weiche
      // unten setzt es in der Capture-Phase, um die Fläche offen zu halten.
      const preventedBefore = event.defaultPrevented;
      onKeyDown?.(event);
      const handledByConsumer = event.defaultPrevented && !preventedBefore;
      if (event.key !== 'Escape' || handledByConsumer || !hasValue) return;
      event.preventDefault();
      // Nur geschluckt, wenn wirklich geleert wurde — im leeren Feld behält Escape
      // seine übliche Bedeutung (Dialog schließen).
      event.stopPropagation();
      clear();
    },
  };
}

/**
 * Für Radix-Flächen (Dialog/Popover), die ein solches Feld enthalten können:
 * `true`, wenn der Tastendruck gerade ein Feld leert und die Fläche deshalb
 * offen bleiben soll.
 *
 *   <Popover.Content onEscapeKeyDown={(e) => { if (escapeWouldClearField()) e.preventDefault(); }}>
 *
 * Nötig, weil Radix Escape auf `document` in der Capture-Phase abfängt — der
 * Handler des Felds läuft erst danach und käme zum Verhindern zu spät. Ohne
 * diesen Aufruf würde ein Escape das Feld leeren UND die Fläche schließen.
 */
export function escapeWouldClearField(ownerDocument = typeof document === 'undefined' ? null : document) {
  const el = ownerDocument?.activeElement;
  return el?.getAttribute?.(CLEARABLE_FIELD_ATTR) === 'true';
}
